import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import HotRail from "@/sections/HotRail";
import ResumeDraft from "@/components/ResumeDraft";
import RugProofBadge from "@/components/RugProofBadge";
import VerifiedBadge from "@/components/VerifiedBadge";
import { TrendingUp, Heart, Eye, Sparkles, Star, ShieldCheck, Users, HandCoins } from "lucide-react";

const themeColor = {
  meme: "#FF3E9D",
  utility: "#00D4FF",
  gaming: "#A855F7",
  ai: "#CFFF04",
  defi: "#FFD700",
};

function Stat({ icon: Icon, label, value, color, title, testId }) {
  return (
    <div className="px-3 py-3 min-w-0" title={title} data-testid={testId}>
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#d0d0d0]">
        <Icon size={11} /> {label}
      </div>
      <div className="font-mono text-sm font-semibold mt-1 truncate" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export function CoinCard({ c }) {
  const featured = c.status === "featured";
  const trust = c.trust || {};
  const tipsSol = (c.tips_total_lamports || 0) / 1_000_000_000;
  const trustColor = trust.verified ? "#00ff41" : trust.level === "building" ? "#cfff04" : "#ffffff";
  return (
    <Link
      to={`/c/${c.slug}`}
      data-testid={`coin-${c.slug}`}
      className={`group block border ${
        featured ? "border-[#cfff04]" : "border-[#3a3a3a]"
      } bg-[#121212] hover:bg-[#1a1a1a] transition-colors`}
    >
      {featured && (
        <div className="bg-[#cfff04] text-[#050505] font-mono text-[11px] uppercase tracking-widest px-3 py-1 flex items-center gap-1">
          <Star size={10} fill="currentColor" /> Featured
        </div>
      )}
      <div className="flex items-center gap-4 p-5">
        <div
          className="w-14 h-14 flex-shrink-0 border border-[#3a3a3a] bg-[#050505] overflow-hidden"
          style={{ borderColor: themeColor[c.theme] || "#222" }}
        >
          <img
            src={`${BACKEND_URL}/api/coins/${c.slug}/logo`}
            alt={c.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold uppercase truncate">{c.name}</div>
          <div className="font-mono text-xs text-[#e2e2e2] flex items-center gap-2 flex-wrap">
            <span>${c.ticker} · {c.theme}</span>
            <VerifiedBadge coin={c} compact />
            <RugProofBadge coin={c} compact />
            {c.og_badge && (
              <span className="text-[#FFD700] text-[11px] tracking-widest" data-testid={`og-badge-${c.slug}`}>
                ★ OG
              </span>
            )}
          </div>
        </div>
      </div>
      <div
        className="mx-5 grid grid-cols-3 divide-x divide-[#2a2a2a] border-t border-b border-[#2a2a2a]"
        data-testid={`coin-stats-${c.slug}`}
      >
        <Stat
          icon={ShieldCheck}
          label="Trust"
          value={`${trust.score ?? 0}/100`}
          color={trustColor}
          title={trust.verified ? "Verified — all 7 SolSpur trust checks passed" : `${trust.passed ?? 0} of ${trust.total ?? 7} trust checks passed`}
          testId={`stat-trust-${c.slug}`}
        />
        <Stat
          icon={Users}
          label="Holders"
          value={c.holders == null ? "—" : Number(c.holders).toLocaleString()}
          color="#ffffff"
          title={c.holders == null ? "Holders are counted once the coin is minted on mainnet" : "On-chain holder wallets"}
          testId={`stat-holders-${c.slug}`}
        />
        <Stat
          icon={HandCoins}
          label="Tips"
          value={tipsSol > 0 ? `${tipsSol.toFixed(2)} SOL` : "0 SOL"}
          color={tipsSol > 0 ? "#cfff04" : "#ffffff"}
          title={`${c.tip_count || 0} tip${c.tip_count === 1 ? "" : "s"} from fans`}
          testId={`stat-tips-${c.slug}`}
        />
      </div>
      <div className="px-5 py-3 flex items-center gap-4 font-mono text-[11px] uppercase tracking-wider text-[#d0d0d0]">
        <span className="flex items-center gap-1.5"><Eye size={11} /> {c.views || 0}</span>
        <span className="flex items-center gap-1.5"><Heart size={11} /> {c.likes || 0}</span>
      </div>
    </Link>
  );
}

const SORTS = [
  { id: "trending", label: "Trending", icon: TrendingUp },
  { id: "newest", label: "Newest", icon: Sparkles },
  { id: "top", label: "Most Liked", icon: Heart },
];

export default function Explore() {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("trending");
  const [total, setTotal] = useState(0);
  const [params, setParams] = useSearchParams();
  const verifiedOnly = params.get("verified") === "1";

  useEffect(() => {
    setLoading(true);
    api
      .get(`/coins?sort=${sort}&limit=48${verifiedOnly ? "&verified=1" : ""}`)
      .then((r) => {
        setCoins(r.data.coins);
        setTotal(r.data.total);
      })
      .catch(() => setCoins([]))
      .finally(() => setLoading(false));
  }, [sort, verifiedOnly]);

  const toggleVerified = () => {
    const next = new URLSearchParams(params);
    if (verifiedOnly) next.delete("verified");
    else next.set("verified", "1");
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="explore-page">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <ResumeDraft className="mb-8" />
          <div className="grid grid-cols-12 gap-6 mb-8">
            <div className="col-span-12 md:col-span-8">
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
                // The Yard
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter" data-testid="explore-title">
                {verifiedOnly
                  ? `${total.toLocaleString()} verified coin${total === 1 ? "" : "s"}.`
                  : `${total > 0 ? `${total.toLocaleString()} coins` : "Coins"} launched on SolSpur.`}
              </h1>
              <p className="text-[#e2e2e2] mt-3 max-w-xl">
                {verifiedOnly
                  ? "Mint + freeze authority revoked, Raydium pool verified, whitepaper on file, creator on X."
                  : "Each one started as a 60-second wizard. Click in, trade their paper price, leave a like."}
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 flex md:justify-end items-end">
              <Link
                to="/create"
                data-testid="explore-cta-create"
                className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-6 py-3"
              >
                Launch Yours →
              </Link>
            </div>
          </div>

          <HotRail />

          <div className="flex items-center gap-2 mb-6 border-b border-[#3a3a3a] flex-wrap">
            {SORTS.map((s) => {
              const Icon = s.icon;
              const active = sort === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSort(s.id)}
                  data-testid={`sort-${s.id}`}
                  className={`flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                    active ? "border-[#cfff04] text-[#cfff04]" : "border-transparent text-[#e2e2e2] hover:text-white"
                  }`}
                >
                  <Icon size={12} /> {s.label}
                </button>
              );
            })}
            <button
              onClick={toggleVerified}
              aria-pressed={verifiedOnly}
              data-testid="verified-only-toggle"
              data-state={verifiedOnly ? "on" : "off"}
              className={`ml-auto my-2 flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase tracking-widest transition-colors ${
                verifiedOnly
                  ? "border-[#00ff41] bg-[#00ff41]/10 text-[#00ff41]"
                  : "border-[#3a3a3a] text-[#e2e2e2] hover:border-[#00ff41] hover:text-[#00ff41]"
              }`}
            >
              <ShieldCheck size={13} /> Verified only
              <span
                className={`w-7 h-4 border relative ${verifiedOnly ? "border-[#00ff41]" : "border-[#3a3a3a]"}`}
                aria-hidden="true"
              >
                <span
                  className={`absolute top-0.5 w-2.5 h-2.5 transition-all ${
                    verifiedOnly ? "left-[14px] bg-[#00ff41]" : "left-0.5 bg-[#d0d0d0]"
                  }`}
                />
              </span>
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={`explore-skeleton-${i}`} className="border border-[#3a3a3a] bg-[#121212] h-52 animate-pulse" />
              ))}
            </div>
          ) : coins.length === 0 && verifiedOnly ? (
            <div className="border border-[#00ff41]/40 bg-[#121212] py-14 px-8 text-center" data-testid="explore-verified-empty">
              <ShieldCheck size={28} className="mx-auto text-[#00ff41] mb-4" />
              <p className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-3">
                No verified coins yet.
              </p>
              <p className="text-[#e2e2e2] text-sm max-w-md mx-auto mb-6">
                Verified means all 7 trust checks pass: mint + freeze revoked, Raydium pool verified on-chain,
                whitepaper on file, creator on X. See who&apos;s close.
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/verified"
                  data-testid="explore-verified-empty-link"
                  className="btn-brutal inline-flex bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-sm px-6 py-3"
                >
                  Almost-verified coins →
                </Link>
                <button
                  onClick={toggleVerified}
                  data-testid="explore-verified-empty-clear"
                  className="border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors"
                >
                  Show all coins
                </button>
              </div>
            </div>
          ) : coins.length === 0 ? (
            <div className="border border-[#cfff04] bg-gradient-to-b from-[#0a0a0a] to-[#050505] py-16 px-8 text-center" data-testid="explore-empty">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-4">
                // First mover advantage
              </div>
              <p className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mb-3">
                Be coin <span className="text-[#cfff04]">#1</span>.
              </p>
              <p className="text-[#e2e2e2] mb-2 text-sm max-w-md mx-auto">
                The yard is empty. Whoever launches first gets pinned to the top of /explore
                for the first 100 visitors and a permanent &ldquo;Founding Coin&rdquo; badge.
              </p>
              <p className="text-[#d0d0d0] mb-8 text-sm">
                💡 Use code <span className="text-[#cfff04]">LAUNCH20</span> for 20% off publish ($15.20)
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  to="/create"
                  data-testid="explore-empty-cta"
                  className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4 items-center gap-2"
                >
                  <Sparkles size={16} /> Claim Coin #1
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto text-left">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04]">Step 1</div>
                  <div className="text-xs text-[#e2e2e2] mt-1">Name &amp; theme. ~60 sec.</div>
                </div>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04]">Step 2</div>
                  <div className="text-xs text-[#e2e2e2] mt-1">AI logo + whitepaper.</div>
                </div>
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04]">Step 3</div>
                  <div className="text-xs text-[#e2e2e2] mt-1">Publish for $15.20 with LAUNCH20.</div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
              data-testid="explore-grid"
            >
              {coins.map((c) => (
                <CoinCard key={c.slug} c={c} />
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
